import { RateConfResult } from '@numeo/types-core'
import { Box, Button, Card, Chip, Grid, Table, Typography } from '@mui/joy'
import DownloadIcon from '@mui/icons-material/Download'
import React from 'react'

interface RateconfViewerProps {
    fileUrl: string
    extraction: RateConfResult
    projectName: string
    emailDate: number
    emailSubject: string
}

export const RateconfViewer: React.FC<RateconfViewerProps> = ({ fileUrl, extraction, projectName, emailDate, emailSubject }) => {
    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleString()
    }

    const handleDownload = () => {
        window.open(fileUrl, '_blank')
    }

    return (
        <Grid container spacing={2}>
            {/* PDF Viewer */}
            <Grid xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography level="h4">Document Preview</Typography>
                        <Button size="sm" variant="outlined" startDecorator={<DownloadIcon />} onClick={handleDownload}>
                            Download
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            height: 'calc(100vh - 400px)',
                            minHeight: 600,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 'sm',
                            overflow: 'hidden',
                        }}
                    >
                        <iframe src={`${fileUrl}#toolbar=0`} style={{ width: '100%', height: '100%', border: 'none' }} title="Rateconf PDF" />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            Project: {projectName}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            Email Date: {formatDate(emailDate)}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                            Subject: {emailSubject}
                        </Typography>
                    </Box>
                </Card>
            </Grid>

            {/* Extracted Fields */}
            <Grid xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
                    <Typography level="h4" sx={{ mb: 2 }}>
                        Extracted Fields
                    </Typography>

                    {/* Document ID */}
                    <Box sx={{ mb: 3 }}>
                        <Typography level="title-md" sx={{ mb: 1 }}>
                            Document ID
                        </Typography>
                        <Typography level="body-md" sx={{ fontWeight: 'bold', color: extraction.documentId ? 'success.500' : 'danger.500' }}>
                            {extraction.documentId || 'Not found'}
                        </Typography>
                    </Box>

                    {/* Broker */}
                    {extraction.broker && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Broker
                            </Typography>
                            <Typography level="body-sm">Company: {extraction.broker.companyName || 'N/A'}</Typography>
                            <Typography level="body-sm">MC: {extraction.broker.mcNumber || 'N/A'}</Typography>
                            {extraction.broker.contact && (
                                <>
                                    <Typography level="body-sm">Contact: {extraction.broker.contact.name || 'N/A'}</Typography>
                                    <Typography level="body-sm">Phone: {extraction.broker.contact.phone || 'N/A'}</Typography>
                                    <Typography level="body-sm">Email: {extraction.broker.contact.email || 'N/A'}</Typography>
                                </>
                            )}
                        </Box>
                    )}

                    {/* Carrier */}
                    {extraction.carrier && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Carrier
                            </Typography>
                            <Typography level="body-sm">Company: {extraction.carrier.companyName || 'N/A'}</Typography>
                        </Box>
                    )}

                    {/* Route */}
                    {extraction.route && extraction.route.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Route ({extraction.route.length} stops)
                            </Typography>
                            <Table size="sm" variant="outlined">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Location</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extraction.route.map((stop, idx) => (
                                        <tr key={idx}>
                                            <td>
                                                <Chip size="sm" color={stop.type === 'pickup' ? 'primary' : 'success'}>
                                                    {stop.type}
                                                </Chip>
                                            </td>
                                            <td>
                                                {stop.location?.city}, {stop.location?.state}
                                            </td>
                                            <td>{stop.date || 'N/A'}</td>
                                            <td>{stop.time || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Box>
                    )}

                    {/* Freight */}
                    {extraction.freightDetails && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Freight Details
                            </Typography>
                            <Typography level="body-sm">Type: {extraction.freightDetails.type || 'N/A'}</Typography>
                            <Typography level="body-sm">Content: {extraction.freightDetails.content || 'N/A'}</Typography>
                            {extraction.freightDetails.weight && (
                                <Typography level="body-sm">
                                    Weight: {extraction.freightDetails.weight.value} {extraction.freightDetails.weight.unit}
                                </Typography>
                            )}
                            <Typography level="body-sm">Quantity: {extraction.freightDetails.quantity || 'N/A'}</Typography>
                        </Box>
                    )}

                    {/* Rate */}
                    {extraction.rate && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Rate
                            </Typography>
                            <Typography level="h3" sx={{ color: 'success.500' }}>
                                ${extraction.rate.amount?.toLocaleString() || 'N/A'} {extraction.rate.currency || 'USD'}
                            </Typography>
                        </Box>
                    )}

                    {/* Total Distance */}
                    {extraction.totalDistance && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Total Distance
                            </Typography>
                            <Typography level="body-md">{extraction.totalDistance} miles</Typography>
                        </Box>
                    )}

                    {/* Notes */}
                    {extraction.notes && (
                        <Box sx={{ mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Notes
                            </Typography>
                            <Typography level="body-sm">{extraction.notes}</Typography>
                        </Box>
                    )}
                </Card>
            </Grid>
        </Grid>
    )
}
